import type { AbilityTarget } from './class-types.js';

/**
 * Actions every hero has, whatever the class. Each costs one action pip and takes `HERO_ACTION_SECONDS` before it happens, except the stances
 * (Anticipate Danger, Hide, Run), free to switch with a toggle cooldown.
 * Player-controlled, except Anticipate Danger, which the AI can autocast. Rules live in `src/server/game/combat/tactical-system.ts`.
 */
export type TacticalActionId = 'anticipate-danger' | 'hide' | 'run' | 'create-opening' | 'assess';
export interface TacticalActionDefinition {
  id: TacticalActionId; name: string; description: string; target: AbilityTarget;
  /** A stance is switched on with a button press and off with the next one; it holds until released. */
  stance?: boolean;
  /** The AI can use it by itself; the ability bar shows an autocast toggle. */
  autocast?: boolean;
}
export const TACTICAL_ACTIONS: TacticalActionDefinition[] = [
  { id: 'anticipate-danger', name: 'Anticipate Danger', target: 'self', stance: true, autocast: true,
    description: 'A defensive stance, in a fight or out of one. Incoming Primary Dice are lowered by one while it holds, and one action pip stays locked. It starts and stops at once, free of cost; switching it takes a two second cooldown.' },
  { id: 'hide', name: 'Hide', target: 'self', stance: true,
    description: 'Sneak at half pace and hold your attacks. In a fight, whenever no foe can see you, you hide for one action: foes leave you be while others stand, and your first attack has advantage, then you are seen. A foe that comes round your cover spots you.' },
  { id: 'run', name: 'Run', target: 'self', stance: true,
    description: 'Move at double pace; actions come back half as fast. Hide is its opposite: switching one on ends the other. Ends when a fight begins, and a Dying hero cannot run.' },
  { id: 'create-opening', name: 'Create an Opening', target: 'enemy',
    description: 'Expose a foe. The next hero attack against it, from anyone, raises its Primary Die by one. A natural critical keeps the opening. Fades after six seconds.' },
  { id: 'assess', name: 'Assess', target: 'enemy',
    description: 'A Perception check reveals the foe’s exact hit points and the damage types it resists or is weak to, to the whole party. Two second cooldown; a failed check can be repeated.' },
];
export const isTacticalAction = (id: string): id is TacticalActionId => TACTICAL_ACTIONS.some(action => action.id === id);
/** Anticipate Danger, Hide and Run: switched on and off, independent of the hero's orders. */
export const isStance = (id: string) => TACTICAL_ACTIONS.some(action => action.id === id && action.stance);
/** Seconds after an Assess before the same hero can Assess again. */
export const ASSESS_COOLDOWN_SECONDS = 3;
/** Seconds after switching Anticipate Danger on or off before the same hero can switch it again. */
export const ANTICIPATE_COOLDOWN_SECONDS = 3;
/** Seconds after switching Hide on or off before the same hero can switch it again. */
export const HIDE_COOLDOWN_SECONDS = 2;
/** Seconds a hidden hero can stay in a foe's sight before it is spotted, so stepping out to strike can still land the hidden attack. */
export const SPOT_SECONDS = .75;
/** How long a foe struck from hiding hunts its attacker, whoever else stands. */
export const HUNT_SECONDS = 6;
/** A strike from hiding that kills sets the nearest crewmate within this many metres of the body hunting the attacker. */
export const AVENGE_RANGE = 10;
/** Any hero attack is heard by foes this close to the attacker (metres), who glance toward it this long without changing target. */
export const NOISE_RANGE = 6, GLANCE_SECONDS = 2;
/** A hero holding Hide walks at this share of its pace. */
export const HIDE_PACE = .5;
/** Seconds after switching Run on or off before the same hero can switch it again. */
export const RUN_COOLDOWN_SECONDS = 3;
/** A hero running moves at this multiple of its pace. */
export const RUN_PACE = 2;
/** A running hero takes this many times the usual recovery to regain an action. */
export const RUN_RECOVERY = 2;
/** Seconds Create an Opening stays on its target. */
export const OPENING_SECONDS = 6;
/** Difficulty of the Perception check an Assess makes. */
export const ASSESS_DC = 10;
